<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
// NOTICE: KAPTION-INTEGRATION
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { emitter } from 'shared/helpers/mitt';
import ConversationApi from 'dashboard/api/conversations';
import KaptionScheduledMessagesAPI from 'dashboard/api/kaptionScheduledMessages';
import { getNewConversationDefaults } from 'dashboard/api/kaptionNewConversation';
import {
  createNewContact,
  fetchContactableInboxes,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import {
  renderTemplateBody,
  buildTemplateParams,
  buildWhatsAppConversationPayload,
  buildScheduledMessagePayload,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';

import RecipientStep from './steps/RecipientStep.vue';
import TemplateGalleryStep from './steps/TemplateGalleryStep.vue';
import ComposePreviewStep from './steps/ComposePreviewStep.vue';
import CreateTemplateStep from './steps/CreateTemplateStep.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { canUseFeature } = useFeaturePermissions();
const router = useRouter();
const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const getWhatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const getDirectMessageTemplates = useMapGetter(
  'inboxes/getDirectMessageTemplates'
);
const labels = useMapGetter('labels/getLabels');

const step = ref(1);
const selectedInbox = ref(null);
const selectedTemplate = ref(null);
const isSending = ref(false);
const createdContactExtras = ref(null);
const scheduledConversation = ref(null);
const inferredCountry = ref('');
let defaultsRequest = 0;
const isCreatingTemplate = ref(false);
const composeState = reactive({
  bodyValues: {},
  headerMedia: {},
  isValid: false,
});

const recipient = ref({
  mode: 'contact',
  rawInput: '',
  contact: null,
  e164: '',
  isValid: false,
});

const waInboxes = computed(() => getWhatsAppInboxes.value || []);

// new-conversation-modal.GALLERY.1
const templates = computed(() => {
  if (!selectedInbox.value) return [];
  return getDirectMessageTemplates.value(selectedInbox.value.id) || [];
});

const templateNeedsApproval = computed(
  () =>
    String(selectedTemplate.value?.status || '').toUpperCase() !== 'APPROVED'
);

const steps = computed(() => [
  { index: 1, label: t('NEW_CONVERSATION_MODAL.STEP.TO') },
  { index: 2, label: t('NEW_CONVERSATION_MODAL.STEP.TEMPLATE') },
  { index: 3, label: t('NEW_CONVERSATION_MODAL.STEP.PREVIEW') },
]);

const goToTemplate = () => {
  step.value = 2;
};

const selectTemplate = template => {
  selectedTemplate.value = template;
  step.value = 3;
};

const onTemplateCreated = template => {
  isCreatingTemplate.value = false;
  selectTemplate(template);
};

const canCreateTemplate = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT') &&
    canUseFeature('whatsapp_template_management')
);

const onComposeChange = payload => {
  Object.assign(composeState, payload);
};

const close = () => emit('close');

// Resolve the WhatsApp source_id for the (possibly newly created) contact by
// looking up its contactable inboxes and matching the selected inbox.
const resolveSourceId = async (contactId, fallback) => {
  try {
    const inboxes = await fetchContactableInboxes(contactId);
    const match = inboxes.find(i => i.id === selectedInbox.value.id);
    if (match?.sourceId) return match.sourceId;
  } catch (error) {
    // fall through to fallback
  }
  return fallback;
};

// new-conversation-modal.SEND.1 — Create the conversation with the rendered
// template as its first message, then jump into it.
const sendNow = async ({
  contact,
  sourceId,
  bodyValues,
  headerMedia,
  buttonValues,
}) => {
  const content = renderTemplateBody(selectedTemplate.value, bodyValues);
  const templateParams = buildTemplateParams(
    selectedTemplate.value,
    bodyValues,
    headerMedia,
    buttonValues
  );
  const payload = buildWhatsAppConversationPayload({
    inbox: selectedInbox.value,
    contact,
    sourceId,
    content,
    templateParams,
    currentUser: currentUser.value,
  });
  return store.dispatch('contactConversations/create', {
    params: payload,
    isFromWhatsApp: true,
  });
};

// new-conversation-modal.SCHEDULE.1 — Create an empty conversation shell (no
// message is sent now — see conversations_controller create: MessageBuilder
// only runs when a message param is present), then create a scheduled template
// message on it.
const scheduleLater = async ({
  contact,
  sourceId,
  bodyValues,
  headerMedia,
  buttonValues,
  schedule,
}) => {
  const key = `${contact.id}:${selectedInbox.value.id}`;
  if (scheduledConversation.value?.key !== key) {
    const { data: conversation } = await ConversationApi.create({
      inbox_id: selectedInbox.value.id,
      contact_id: contact.id,
      source_id: sourceId,
      ...(canUseFeature('direct_assignment')
        ? { assignee_id: currentUser.value?.id }
        : {}),
    });
    scheduledConversation.value = { key, conversation };
  }
  const { conversation } = scheduledConversation.value;
  const templateParams = buildTemplateParams(
    selectedTemplate.value,
    bodyValues,
    headerMedia,
    buttonValues
  );
  await KaptionScheduledMessagesAPI.createScheduledMessage(
    currentAccountId.value,
    conversation.id,
    {
      ...buildScheduledMessagePayload({
        template: selectedTemplate.value,
        bodyValues,
        scheduledAt: schedule.at,
        timezone: schedule.timezone,
      }),
      message_content: renderTemplateBody(selectedTemplate.value, bodyValues),
      template_language: selectedTemplate.value.language,
      template_params: templateParams,
      approval_required: templateNeedsApproval.value,
    }
  );
  emitter.emit('kaption-message-scheduled', {
    conversationId: conversation.id,
  });
  return conversation;
};

const handleSend = async ({
  bodyValues,
  headerMedia,
  buttonValues,
  schedule,
}) => {
  if (isSending.value) return;
  isSending.value = true;
  try {
    // Create-on-send: ensure we have a contact id.
    let contact = recipient.value.contact;
    if (!contact?.id) {
      const draft = recipient.value.contactDraft || {};
      contact = await createNewContact({
        phoneNumber: recipient.value.e164,
        firstName: draft.firstName,
        lastName: draft.lastName,
        email: draft.email,
        inboxId: selectedInbox.value.id,
        sourceId: recipient.value.e164.replace(/^\+/, ''),
      });
      recipient.value = { ...recipient.value, contact };
      createdContactExtras.value = {
        contactId: contact.id,
        labels: false,
        note: false,
      };
    }
    const draft = recipient.value.contactDraft || {};
    const extras = createdContactExtras.value;
    if (extras?.contactId === contact.id) {
      if (draft.labels?.length && !extras.labels) {
        await store.dispatch('contactLabels/update', {
          contactId: contact.id,
          labels: draft.labels,
        });
        extras.labels = true;
      }
      if (draft.note?.trim() && !extras.note) {
        await store.dispatch('contactNotes/create', {
          contactId: contact.id,
          content: draft.note.trim(),
        });
        extras.note = true;
      }
    }

    // Resolve the WhatsApp source_id for this contact + inbox.
    const fallbackSource = recipient.value.e164.replace(/^\+/, '');
    const sourceId = await resolveSourceId(contact.id, fallbackSource);

    const effectiveSchedule =
      schedule ||
      (templateNeedsApproval.value
        ? {
            at: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          }
        : null);
    const conversation = effectiveSchedule
      ? await scheduleLater({
          contact,
          sourceId,
          bodyValues,
          headerMedia,
          buttonValues,
          schedule: effectiveSchedule,
        })
      : await sendNow({
          contact,
          sourceId,
          bodyValues,
          headerMedia,
          buttonValues,
        });

    // Jump straight into the (new or scheduled) conversation.
    close();
    useAlert(
      t(
        schedule
          ? 'NEW_CONVERSATION_MODAL.SCHEDULE.SUCCESS'
          : 'NEW_CONVERSATION_MODAL.SUCCESS'
      )
    );
    router.push(
      `/app/accounts/${conversation.account_id}/conversations/${conversation.id}`
    );
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('NEW_CONVERSATION_MODAL.ERROR')
    );
  } finally {
    isSending.value = false;
  }
};

watch(waInboxes, inboxes => {
  // Inbox auto-pick when there is exactly one WhatsApp inbox.
  if (!selectedInbox.value && inboxes.length === 1) {
    [selectedInbox.value] = inboxes;
  }
});

watch(selectedInbox, async inbox => {
  if (!inbox || !currentAccountId.value) return;
  defaultsRequest += 1;
  const request = defaultsRequest;
  try {
    const { data } = await getNewConversationDefaults(
      currentAccountId.value,
      inbox.id
    );
    if (request === defaultsRequest && selectedInbox.value?.id === inbox.id) {
      inferredCountry.value = data.country || '';
    }
  } catch {
    // Inbox-number fallback remains available in RecipientStep.
  }
});

onMounted(async () => {
  await store.dispatch('inboxes/get');
  store.dispatch('labels/get').catch(() => {});
  if (waInboxes.value.length) {
    try {
      const { data } = await getNewConversationDefaults(currentAccountId.value);
      selectedInbox.value =
        waInboxes.value.find(item => item.id === data.preferred_inbox_id) ||
        waInboxes.value[0];
      inferredCountry.value = data.country || 'US';
    } catch {
      [selectedInbox.value] = waInboxes.value;
    }
  }
});

useKeyboardEvents({ Escape: { action: close } });
</script>

<template>
  <!-- Frosted backdrop -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2 backdrop-blur-[6px]"
    data-testid="new-conversation-modal"
    @click.self="close"
  >
    <!-- Glass tile surface -->
    <div
      class="w-[46rem] max-w-full max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden bg-n-alpha-3 border border-n-strong shadow-2xl backdrop-blur-[100px] rounded-2xl"
    >
      <!-- Header -->
      <div
        class="flex items-start justify-between gap-4 px-6 pt-5 pb-4 shrink-0"
      >
        <div class="flex items-center min-w-0 gap-3">
          <span
            class="flex items-center justify-center rounded-xl size-9 shrink-0 bg-n-brand/10 text-n-brand"
          >
            <span class="i-lucide-message-square-plus size-[1.15rem]" />
          </span>
          <div class="min-w-0">
            <h2 class="text-base font-semibold leading-tight text-n-slate-12">
              {{ t('NEW_CONVERSATION_MODAL.TITLE') }}
            </h2>
            <p class="text-xs leading-tight text-n-slate-10">
              {{ t('NEW_CONVERSATION_MODAL.SUBTITLE') }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="flex items-center justify-center transition-colors rounded-lg size-10 shrink-0 text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12"
          data-testid="modal-close"
          :aria-label="t('NEW_CONVERSATION_MODAL.BACK')"
          @click="close"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <!-- Stepper -->
      <div
        class="flex items-center gap-2 px-6 pb-4 border-b shrink-0 border-n-weak"
      >
        <template v-for="(s, idx) in steps" :key="s.index">
          <div class="flex items-center gap-2 shrink-0">
            <span
              class="flex items-center justify-center rounded-full size-5 text-[10px] font-semibold transition-colors"
              :class="{
                'bg-n-teal-9 text-white': step > s.index,
                'bg-n-brand text-white': step === s.index,
                'bg-n-alpha-black2 text-n-slate-10': step < s.index,
              }"
            >
              <span v-if="step > s.index" class="i-lucide-check size-3" />
              <template v-else>{{ s.index }}</template>
            </span>
            <span
              class="text-xs font-medium transition-colors"
              :class="step >= s.index ? 'text-n-slate-12' : 'text-n-slate-10'"
            >
              {{ s.label }}
            </span>
          </div>
          <span
            v-if="idx < steps.length - 1"
            class="h-px flex-1 min-w-[1rem] transition-colors"
            :class="step > s.index ? 'bg-n-teal-9/50' : 'bg-n-weak'"
          />
        </template>
      </div>

      <!-- Body (scrolls if tall) -->
      <div class="px-6 py-5 overflow-y-auto">
        <RecipientStep
          v-if="step === 1"
          v-model:recipient="recipient"
          :inbox="selectedInbox"
          :wa-inboxes="waInboxes"
          :inferred-country="inferredCountry"
          :labels="labels"
          @update:inbox="selectedInbox = $event"
          @next="goToTemplate"
        />

        <CreateTemplateStep
          v-else-if="step === 2 && isCreatingTemplate"
          :inbox="selectedInbox"
          @created="onTemplateCreated"
          @back="isCreatingTemplate = false"
        />

        <TemplateGalleryStep
          v-else-if="step === 2"
          :templates="templates"
          :selected-template="selectedTemplate"
          :can-create="canCreateTemplate"
          @select="selectTemplate"
          @create="isCreatingTemplate = true"
          @back="step = 1"
        />

        <ComposePreviewStep
          v-else-if="step === 3 && selectedTemplate"
          :template="selectedTemplate"
          :contact="recipient.contact"
          :inbox-name="selectedInbox?.name"
          :is-sending="isSending"
          @change="onComposeChange"
          @back="step = 2"
          @send="handleSend"
        />
      </div>
    </div>
  </div>
</template>
